import { useState } from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";

const THREAT_CARDS = [
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
        <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#EF4444" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M2 17l10 5 10-5" stroke="#EF4444" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M2 12l10 5 10-5" stroke="#EF4444" strokeWidth="1.5" strokeLinejoin="round"/>
      </svg>
    ),
    title: "No Serialization Verification",
    consequence: "FDA can issue a Warning Letter, halt your ability to receive product, and refer your case for criminal prosecution. Each unverified package is a separate violation.",
  },
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="#F59E0B" strokeWidth="1.5"/>
        <path d="M8 12h8M8 8h8M8 16h4" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M17 17l3 3" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    title: "Missing T3 Documents",
    consequence: "Transaction Information, Transaction History, and Transaction Statements (T3) must be stored for 6 years. Missing records can result in fines up to $250,000 per violation.",
  },
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
        <path d="M4 7h16M4 12h16M4 17h7" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="18" cy="17" r="3" stroke="#EF4444" strokeWidth="1.5"/>
        <path d="M18 15.5v1.5l1 1" stroke="#EF4444" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
    title: "Uninspected Returns",
    consequence: "Returned product must be quarantined and verified before re-entering your dispensable stock. Failure to do so exposes you to diversion liability and DSCSA enforcement.",
  },
  {
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
        <path d="M12 9v4M12 17h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#EF4444" strokeWidth="1.5" strokeLinejoin="round"/>
      </svg>
    ),
    title: "No Recall Traceability",
    consequence: "When FDA issues a Class I recall, you have hours — not days — to identify and quarantine affected units. Without traceability records, you cannot respond in time and may face license suspension.",
  },
];

export default function DSCSALanding() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!email.trim() || !phone.trim()) {
      setError("Both email and phone number are required.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/dscsa/request-resource", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), phone: phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Something went wrong. Please try again.");
      } else {
        setSubmitted(true);
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <SEO
        title="DSCSA Compliance for Pharmaceutical Buyers — Free Checklist"
        description="If you purchase pharmaceuticals, DSCSA enforcement applies to you. FDA can issue fines up to $250,000 per violation. Download the free DSCSA Compliance Review and Checklist from Premier Pharma."
        path="/dscsa"
        keywords="DSCSA compliance, pharmaceutical buyer compliance, drug supply chain security, FDA enforcement, DSCSA checklist, pharmaceutical traceability"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "DSCSA Compliance", path: "/dscsa" },
        ]}
      />

      {/* HERO */}
      <section className="relative bg-primary overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cpath d='M0 0h40v40H0z'/%3E%3Cpath d='M20 0v40M0 20h40' stroke='%23ffffff' stroke-width='0.5'/%3E%3C/g%3E%3C/svg%3E\")",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
          <span className="inline-flex items-center gap-2 bg-red-500/20 border border-red-400/30 text-red-300 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
            <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse motion-reduce:animate-none" />
            FDA Enforcement Active
          </span>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            If You Purchase Pharmaceuticals,<br />
            <span className="text-red-400">You Could Be Shut Down Tomorrow</span>
          </h1>

          <p className="text-white/75 text-lg sm:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
            DSCSA interoperability requirements have been fully in effect since <strong className="text-white">November 27, 2023</strong>. The FDA is actively issuing Warning Letters and referrals for criminal prosecution. Are you prepared?
          </p>

          {/* Stat block */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-10">
            {[
              { stat: "$250,000", label: "Max fine — first violation ($1M for repeat)" },
              { stat: "6 Years", label: "T2 record retention required" },
              { stat: "Nov 2023", label: "Full enforcement in effect" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-xl px-6 py-5"
              >
                <div className="text-3xl font-bold text-red-400 mb-1">{s.stat}</div>
                <div className="text-white/70 text-sm">{s.label}</div>
              </div>
            ))}
          </div>

          <a
            href="#checklist"
            className="inline-flex items-center gap-2 bg-accent hover:bg-blue-600 text-white rounded-btn px-8 py-4 font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-accent/25 hover:-translate-y-0.5"
          >
            Get Your Free Compliance Checklist
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
          </a>
        </div>
      </section>

      {/* THREAT CARDS */}
      <section className="py-16 bg-[#F6F5F3]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary mb-4">
              Four Ways Your Operation Is at Risk Right Now
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              DSCSA enforcement isn't hypothetical. These are the specific failure modes that trigger FDA action — and the consequences that follow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {THREAT_CARDS.map((card, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
                    {card.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-primary mb-2">{card.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{card.consequence}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUTHORITY BAR */}
      <section className="py-12 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-gray-400 mb-8">
            Enforcement context from leading regulatory bodies
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                org: "FDA",
                detail:
                  "The FDA's Office of Criminal Investigations actively pursues diversion, counterfeiting, and DSCSA non-compliance as federal criminal matters — not just administrative fines.",
              },
              {
                org: "NABP",
                detail:
                  "The NABP's Verified-Accredited Wholesale Distributors (VAWD) program requires full DSCSA compliance. Loss of VAWD accreditation signals state licensing boards — and can directly trigger a license revocation review.",
              },
              {
                org: "DEA",
                detail:
                  "The DEA coordinates with FDA when supply chain irregularities suggest diversion of controlled substances. An FDA DSCSA investigation that uncovers handling gaps can escalate into a parallel DEA inquiry for Schedule II/III products.",
              },
            ].map((item, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{item.org}</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section id="checklist" className="py-20 bg-[#F6F5F3]">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <div className="bg-white rounded-2xl border border-green-100 shadow-sm p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-primary mb-3">Checklist Sent!</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Check your inbox — the DSCSA Compliance Review and Checklist PDF is on its way. If you don't see it within a few minutes, check your spam folder.
              </p>
              <p className="text-gray-500 text-sm mb-8">
                Want to eliminate DSCSA risk entirely? Premier Pharma includes a full compliance platform free with qualifying purchases.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-accent hover:bg-blue-600 text-white rounded-btn px-8 py-3 font-semibold transition-all duration-200"
              >
                Open a Free Account
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 sm:p-10">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-accent" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2a10 10 0 100 20A10 10 0 0012 2z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-primary mb-2">
                  Get Your Free DSCSA Compliance Checklist
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We'll email you the full DSCSA Compliance Review and Checklist PDF immediately — no account required. Use it to audit your compliance posture before the FDA does.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="dscsa-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="dscsa-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourcompany.com"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition"
                  />
                </div>

                <div>
                  <label htmlFor="dscsa-phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="dscsa-phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 000-0000"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition"
                  />
                </div>

                {error && (
                  <p className="text-red-600 text-sm bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-accent hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-btn py-3.5 font-semibold text-base transition-all duration-200 hover:shadow-lg hover:shadow-accent/25"
                >
                  {loading ? "Sending…" : "Send Me the Checklist"}
                </button>

                <p className="text-center text-xs text-gray-400">
                  We'll email you the PDF right away. No spam, no account required.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-16 bg-primary">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Want Zero DSCSA Risk — Permanently?
          </h2>
          <p className="text-white/70 text-lg mb-8 leading-relaxed">
            Premier Pharma includes a full DSCSA compliance platform at no additional cost for customers purchasing $500+/month. Serialization verification, T3 document storage, and recall traceability — all built in.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-accent hover:bg-blue-600 text-white rounded-btn px-8 py-4 font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-accent/25 hover:-translate-y-0.5"
          >
            Open a Free Account
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
          </Link>
        </div>
      </section>
    </>
  );
}
